import React, {
  useState,
  useContext,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import classNames from 'classnames';
import { recordImpressionByName } from '@JohnLewisPartnership/experiments-utils';
import { FlyoutMenu } from '../flyout-menu/FlyoutMenu';
import { EnvVarsFeaturesContext } from '../../../scaffold/EnvVarsFeaturesContext';
import { UserContext } from '../../../scaffold/UserContext';
import styles from './DesktopMenu.scss';
import { isTouchDevice } from '../../../utils/device/identifyDevice';
import { DesktopMenuPanel } from './DesktopMenuPanel';
import { SelectionEvent } from '../desktop-menu-item/DesktopMenuItem';
import { DesktopMenuContentPanel } from './DesktopMenuContentPanel';
import { DesktopMenuTabs } from './DesktopMenuTabs';
import { Category, DESKTOP_MENU_TABS } from '../constants';
import { filterMenuData } from '../../../utils/menu/filterMenuData';
import { formatTestId } from '../../../utils/test-id/format-testid';
import { DESKTOP_MI, NAV_EXP_PARAMS_PROP } from '../../../utils/constants';
import { getExperiencesWithMatchingParamValues } from '../../../utils/monetate/getMonetateParams';
import { useTopLevelTabLoopFocus } from '../hooks/useTopLevelTabLoopFocus';

type DesktopMenuProps = {
  data: MenuItem[];
};

export const DesktopMenu = ({ data }: DesktopMenuProps) => {
  const {
    envVars: { monetate },
    features: { monetateExperiences },
    stickySearchHeader: hasStickyHeader,
  } = useContext(EnvVarsFeaturesContext);
  const {
    userState: {
      user: { monetateId },
    },
  } = useContext(UserContext);

  const [activeTab, setActiveTab] = useState('');
  const [focusedTab, setFocusedTab] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const [focusableIndex, setFocusableIndex] = useState(0);
  const [focusFlyoutOnMount, setFocusFlyoutOnMount] = useState(false);
  const [overlayHeight, setOverlayHeight] = useState<number | undefined>(
    undefined
  );
  const menuRef = useRef<HTMLDivElement | null>(null);
  const tabsRef = useRef(null);
  const panelRef = useRef(null);
  const isMenuOpen = Boolean(activeTab);

  const { focusActiveTab: focusActiveTabButton } = useTopLevelTabLoopFocus({
    containerRef: tabsRef,
    isEnabled: isMenuOpen,
    activeTabSelector: 'button[data-current-tab="true"]',
  });

  // Filter items which should be included in FLYOUT
  const menuItems =
    filterMenuData(data, ['FLYOUT'])
      ?.map(column => ({
        ...column,
        child: filterMenuData(column.child, ['FLYOUT']),
      }))
      ?.filter(x => x.child?.length) || [];

  const tabsToDisplay = useMemo(
    () =>
      DESKTOP_MENU_TABS.filter(tab =>
        menuItems.some(item => item.name === tab)
      ),
    [menuItems]
  );

  // Only an explicitly opened tab controls panel content.
  const menuData = menuItems.find(item => item.name === activeTab)?.child || [];

  const focusTabButtonByName = tab => {
    if (!tab) {
      return;
    }

    const selector = `[data-testid="${formatTestId(tab, 'nav-menu-tab-')}"]`;
    const tabButton = tabsRef.current?.querySelector(selector);
    tabButton?.focus();
  };

  const focusTabButtonByNameOnNextFrame = tab => {
    requestAnimationFrame(() => {
      focusTabButtonByName(tab);
    });
  };

  const focusPanelItemAtIndex = index => {
    const panelItemIndex = Math.max(0, index);
    const links = panelRef.current?.querySelectorAll('a[href], button');
    const target =
      links?.[panelItemIndex] ||
      document.querySelectorAll(
        '#desktop-menu-panel a[href], #desktop-menu-panel button'
      )[panelItemIndex];
    if (!target) {
      return false;
    }
    target.focus();
    return true;
  };

  const focusCurrentPanelItem = () => {
    const currentPanelIndex = activeIndex >= 0 ? activeIndex : focusableIndex;
    return focusPanelItemAtIndex(currentPanelIndex);
  };

  const focusSubmenuForTab = tab => {
    const focusTarget = () => {
      if (isPanelTab(tab)) {
        return focusCurrentPanelItem();
      }

      const firstContentLink = document.querySelector<HTMLAnchorElement>(
        '#desktop-menu a[href]'
      );

      if (!firstContentLink) {
        return false;
      }

      firstContentLink.focus();
      return true;
    };

    const selector = `[data-testid="${formatTestId(tab, 'nav-menu-tab-')}"]`;
    const tabButton = tabsRef.current?.querySelector(selector);

    requestAnimationFrame(() => {
      if (tabButton && document.activeElement !== tabButton) {
        return;
      }

      focusTarget();
    });
  };

  const onSelect = (index, event: SelectionEvent) => {
    setFocusFlyoutOnMount(event.type === 'keydown');
    setFocusableIndex(index);
    setActiveIndex(index);

    if (activeIndex !== index) {
      const categoryName = formatTestId(menuItems?.[index]?.name);
      if (categoryName) {
        const matchingExperiences = getExperiencesWithMatchingParamValues(
          { monetateExperiences },
          NAV_EXP_PARAMS_PROP,
          categoryName
        );

        if (matchingExperiences.length > 0) {
          matchingExperiences.forEach(exp => {
            recordImpressionByName(
              monetate?.channel,
              matchingExperiences as unknown as Parameters<
                typeof recordImpressionByName
              >[1],
              exp.experienceName,
              monetateId
            );
          });
        }
      }
    }
  };

  const onClose = () => {
    if (activeIndex >= 0) setActiveIndex(-1);
    if (activeTab) setActiveTab('');
    if (focusedTab) setFocusedTab('');
    setFocusFlyoutOnMount(false);
    setFocusableIndex(0);
  };

  const handleNavigationMouseLeave = (event: React.MouseEvent<HTMLElement>) => {
    const relatedTarget = event.relatedTarget; // Where pointer/focus has moved to

    // Will only be defined client-side; ignore for server-side rendering
    const leftDocument =
      typeof window !== 'undefined' && relatedTarget === window;

    // Final check tests that the pointer/focus has actually left the
    // current navigation element and is not just moving within it
    if (
      !relatedTarget ||
      (leftDocument && event.target === event.currentTarget) ||
      (typeof relatedTarget === 'object' &&
        'nodeType' in relatedTarget &&
        !event.currentTarget.contains(relatedTarget as Node))
    ) {
      onClose();
    }
  };

  const overlayClassName = classNames(styles.desktopMenuOverlay, {
    [styles.desktopMenuOverlay__active]: activeTab,
  });

  const handleScroll = useCallback(() => {
    requestAnimationFrame(() => {
      const currentScrollPositionPx = window.pageYOffset;
      if (currentScrollPositionPx > 600) {
        setActiveTab('');
      }
    });
  }, []);

  useEffect(
    function manageDesktopMenuOverlayListeners() {
      if (!activeTab) {
        window.removeEventListener('scroll', handleScroll);
        return undefined;
      }

      const updateOverlayHeight = () => {
        setOverlayHeight(document.body.scrollHeight);
      };

      updateOverlayHeight();
      window.addEventListener('resize', updateOverlayHeight);
      window.addEventListener('scroll', handleScroll);

      const handleDocumentPointerDown = (event: PointerEvent) => {
        const target = event.target as Node | null;
        if (!target || menuRef.current?.contains(target)) {
          return;
        }

        onClose();
      };

      document.addEventListener('pointerdown', handleDocumentPointerDown);

      return () => {
        window.removeEventListener('resize', updateOverlayHeight);
        document.removeEventListener('pointerdown', handleDocumentPointerDown);
        window.removeEventListener('scroll', handleScroll);
      };
    },
    [activeTab]
  );

  const isTouch = typeof window !== 'undefined' && isTouchDevice();

  const isPanelTab = tab => [Category.SHOP, Category.BRANDS].includes(tab);
  const isNonPanelTab = tab => Category.OUTLET === tab;
  const isContentPanelTab = tab =>
    [
      Category.NEW_IN,
      Category.SERVICES,
      Category.SERVICES_EXPERIENCES,
      Category.MONEY,
    ].includes(tab);

  const getAnalyticsNavigationPath = (tab, navigationPath = '') => {
    if (!tab || tab === Category.SHOP) {
      return navigationPath;
    }

    if (!navigationPath) {
      return tab;
    }

    const pathParts = navigationPath.split('|');
    if (pathParts[0] === tab) {
      return navigationPath;
    }

    return `${tab}|${navigationPath}`;
  };

  const handleDesktopTabChange = (tab, { focusSubmenu = false } = {}) => {
    const shouldOpenTab = activeTab !== tab;

    setFocusedTab(tab);

    if (activeTab === tab) {
      setActiveTab('');
      setFocusableIndex(0);
      setActiveIndex(-1);
      return;
    }

    if (activeTab !== tab) {
      setActiveTab(tab);

      recordImpressionByName(
        monetate?.channel,
        monetateExperiences as unknown as Parameters<
          typeof recordImpressionByName
        >[1],
        DESKTOP_MI,
        monetateId
      );
    }
    setFocusableIndex(0);
    setActiveIndex(-1);

    if (focusSubmenu && shouldOpenTab) {
      focusSubmenuForTab(tab);
    }
  };

  const focusAdjacentTabButton = (tab, direction = 1) => {
    const currentIndex = tabsToDisplay.indexOf(tab);
    if (currentIndex < 0) {
      return false;
    }

    const nextIndex = currentIndex + direction;
    if (nextIndex < 0 || nextIndex >= tabsToDisplay.length) {
      return false;
    }

    const nextTab = tabsToDisplay[nextIndex];
    setFocusedTab(nextTab);

    const selector = `[data-testid="${formatTestId(nextTab, 'nav-menu-tab-')}"]`;
    const nextButton = tabsRef.current?.querySelector(selector);

    nextButton?.focus();
    return Boolean(nextButton);
  };

  const handleTabButtonKeyDown = (event, tab) => {
    if (event.key === 'Escape' && activeTab) {
      event.preventDefault();
      handleDesktopTabChange(tab);
      focusTabButtonByNameOnNextFrame(tab);
      return;
    }

    if (event.key === 'Tab' && activeTab) {
      onClose();

      if (event.shiftKey) {
        const moved = focusAdjacentTabButton(tab, -1);
        if (moved) {
          event.preventDefault();
        }
        return;
      }

      const moved = focusAdjacentTabButton(tab, 1);
      if (moved) {
        event.preventDefault();
      }
      return;
    }

    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    if (activeTab !== tab) {
      handleDesktopTabChange(tab, { focusSubmenu: true });
      return;
    }

    handleDesktopTabChange(tab);
    focusTabButtonByNameOnNextFrame(tab);
  };

  const handleTabButtonClick = tab => {
    if (activeTab !== tab) {
      handleDesktopTabChange(tab, { focusSubmenu: true });
      return;
    }

    handleDesktopTabChange(tab);
  };

  const handlePanelItemNavigate = () => {};

  const handlePanelItemTabNavigate = (event, index) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      const tabToFocus = activeTab;
      onClose();
      focusTabButtonByNameOnNextFrame(tabToFocus);
      return;
    }

    if (
      event.key === 'Tab' &&
      !event.shiftKey &&
      index === menuData.length - 1
    ) {
      event.preventDefault();
      focusActiveTabButton();
      return;
    }

    if (event.shiftKey) {
      return;
    }
  };

  return (
    <nav
      className={styles.desktopMenu}
      data-testid="navigation-menu"
      onMouseLeave={handleNavigationMouseLeave}
    >
      <div className={styles.pageContainer}>
        <div className={styles.grid} ref={menuRef}>
          <div ref={tabsRef}>
            <DesktopMenuTabs
              activeTab={activeTab}
              isMenuOpen={Boolean(activeTab)}
              onTabFocus={setFocusedTab}
              onActiveTabChange={handleTabButtonClick}
              onActiveTabKeyDown={handleTabButtonKeyDown}
              tabsToDisplay={tabsToDisplay}
              isTouch={isTouch}
            />
          </div>

          {activeTab && (
            <div
              className={classNames(styles.desktopMenuPanels, {
                [styles.desktopMenuPanels__stickyHeader]: hasStickyHeader,
              })}
              id="desktop-menu"
              data-testid="desktop-menu"
            >
              {isPanelTab(activeTab) && (
                <div ref={panelRef}>
                  <DesktopMenuPanel
                    activeTab={activeTab}
                    menuItems={menuData}
                    activeIndex={activeIndex}
                    focusableIndex={focusableIndex}
                    onSelect={onSelect}
                    onClose={onClose}
                    onNavigate={handlePanelItemNavigate}
                    onTabNavigate={handlePanelItemTabNavigate}
                  />
                </div>
              )}

              {isNonPanelTab(activeTab) && menuData[0]?.child.length > 0 && (
                <FlyoutMenu
                  url={menuData[0].url}
                  contentId={menuData[0].contentId}
                  columns={menuData[0].child}
                  featuredContent={menuData[0].featuredContent}
                  focusOnMount={focusFlyoutOnMount}
                  onFocusMountHandled={() => setFocusFlyoutOnMount(false)}
                  hierarchy={getAnalyticsNavigationPath(
                    activeTab,
                    menuData[0].name
                  )}
                  onClose={onClose}
                  onEscape={() => {
                    const tabToFocus = activeTab;
                    onClose();
                    focusTabButtonByNameOnNextFrame(tabToFocus);
                  }}
                  isTouch={isTouch}
                  isWide={true}
                />
              )}

              {isContentPanelTab(activeTab) && (
                <DesktopMenuContentPanel
                  data={menuData}
                  onTabLoop={focusActiveTabButton}
                  hierarchy={getAnalyticsNavigationPath(activeTab)}
                  onEscape={() => {
                    const tabToFocus = activeTab;
                    onClose();
                    focusTabButtonByNameOnNextFrame(tabToFocus);
                  }}
                  isFourColumn={activeTab === Category.MONEY}
                />
              )}

              {isPanelTab(activeTab) &&
                activeIndex >= 0 &&
                menuData[activeIndex].child.length > 0 && (
                  <FlyoutMenu
                    url={menuData[activeIndex].url}
                    contentId={menuData[activeIndex].contentId}
                    columns={menuData[activeIndex].child}
                    featuredContent={menuData[activeIndex].featuredContent}
                    focusOnMount={focusFlyoutOnMount}
                    onFocusMountHandled={() => setFocusFlyoutOnMount(false)}
                    hierarchy={getAnalyticsNavigationPath(
                      activeTab,
                      menuData[activeIndex].name
                    )}
                    onClose={onClose}
                    onTabLoop={focusCurrentPanelItem}
                    onEscape={() => {
                      const tabToFocus = activeTab;
                      onClose();
                      focusTabButtonByNameOnNextFrame(tabToFocus);
                    }}
                    isTouch={isTouch}
                    displayAllCategoryLink={activeTab === Category.SHOP}
                  />
                )}
            </div>
          )}
        </div>
      </div>
      <div
        className={overlayClassName}
        data-testid="desktop-menu-overlay"
        onClick={event => event.stopPropagation()}
        onMouseEnter={onClose}
        style={{
          height: `calc(${overlayHeight}px - var(--jl-header-height))`,
        }}
      />
    </nav>
  );
};
